import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDateRange } from '../utils/dataProcessor';
import { useQueryParams } from '../hooks/useQueryParams';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  resetToDefault: () => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return context;
};

interface DateRangeProviderProps {
  children: ReactNode;
}

export const DateRangeProvider: React.FC<DateRangeProviderProps> = ({ children }) => {
  const { getQueryParam, setMultipleQueryParams } = useQueryParams();
  
  const [dateRange, setDateRangeState] = useState<DateRange>(() => {
    // URL 파라미터에서 날짜 읽기
    const urlStartDate = getQueryParam('startDate');
    const urlEndDate = getQueryParam('endDate');
    
    if (urlStartDate && urlEndDate) {
      console.log('📅 URL에서 날짜 범위 읽기:', { startDate: urlStartDate, endDate: urlEndDate });
      return { startDate: urlStartDate, endDate: urlEndDate };
    }
    
    // URL에 없으면 과거 7일 사용 (過去7日間)
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6); // 오늘 포함 7일
    
    const defaultRange = {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
    
    console.log('📅 기본 날짜 범위 (過去7日間):', defaultRange);
    return defaultRange;
  });

  // 날짜 변경 시 URL도 업데이트
  const setDateRange = (range: DateRange) => {
    setDateRangeState(range);
    setMultipleQueryParams({
      startDate: range.startDate,
      endDate: range.endDate
    });
  };

  const resetToDefault = () => {
    const defaultRange = getDateRange();
    console.log('📅 날짜 범위 리셋:', defaultRange);
    setDateRange(defaultRange);
  };

  console.log('📅 현재 DateRange 상태:', dateRange);

  return (
    <DateRangeContext.Provider value={{ dateRange, setDateRange, resetToDefault }}>
      {children}
    </DateRangeContext.Provider>
  );
}; 